// pages/AILessonGenerator.jsx
// ✅ نسخه نهایی - کاملاً ایزوله با کلاس‌های منحصربه‌فرد

import React, { useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import './AILessonGenerator.css';

// ✅ تنظیم Worker (بدون CDN)
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.min.js';

const AILessonGenerator = () => {
  // ===== STATE‌ها =====
  const [pdfFile, setPdfFile] = useState(null);
  const [grade, setGrade] = useState('');
  const [book, setBook] = useState('');
  const [chapter, setChapter] = useState('');
  const [childAge, setChildAge] = useState(7);
  const [childName, setChildName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadLink, setDownloadLink] = useState('');
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [pdfText, setPdfText] = useState('');

  // ===== ۱. استخراج واقعی متن از PDF =====
  const extractTextFromPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }
    setPdfText(fullText);
    return fullText;
  };

  // ===== ۲. تحلیل محتوا و ساخت پرامپت =====
  const generateSmartPrompt = (text, grade, book, chapter, childAge) => {
    const truncatedText = text.length > 1500 ? text.slice(0, 1500) + '...' : text;

    return `
بر اساس متن زیر، یک تدریس گام‌به‌گام برای کودک ${childAge} ساله در پایه ${grade} و درس "${chapter}" از کتاب "${book}" طراحی کن.

متن: ${truncatedText}

خروجی JSON با ساختار:
{
  "lessonType": "letters",
  "title": "عنوان درس",
  "poem": "شعر کوتاه از متن",
  "steps": [
    {
      "stepNumber": 1,
      "title": "عنوان مرحله",
      "type": "start",
      "teacherTalk": "متن گفتاری معلم",
      "visualDescription": "توضیح تصویر",
      "activity": {
        "type": "question",
        "description": "سوال",
        "options": ["گزینه ۱", "گزینه ۲", "گزینه ۳", "گزینه ۴"],
        "correctAnswer": "گزینه ۱"
      },
      "encouragement": "جمله تشویقی"
    }
  ],
  "keywords": ["کلمه۱", "کلمه۲"],
  "summary": "جمع‌بندی نهایی",
  "homework": "تکلیف منزل"
}
`;
  };

  // ===== ۳. ارسال به AvalAI =====
  const generateLesson = async (prompt, apiKey) => {
    const response = await fetch('https://api.avalai.ir/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: 'gpt-5-mini',
        messages: [
          { 
            role: 'system', 
            content: 'شما یک معلم با تجربه هستید. خروجی را فقط به صورت JSON تولید کنید. هیچ توضیح اضافی ننویسید.'
          },
          { role: 'user', content: prompt }
        ],
        temperature: 0.3,
        max_tokens: 4000,
        service_tier: 'flex',
        response_format: { type: 'json_object' }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`خطا در ارتباط با API: ${response.status} - ${JSON.stringify(errorData)}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content;
    
    try {
      return JSON.parse(content);
    } catch (e) {
      console.error('خروجی خام:', content);
      throw new Error('خروجی هوش مصنوعی JSON معتبر نیست');
    }
  };

  // ===== ۴. تولید تصویر =====
  const generateImage = async (prompt, apiKey) => {
    try {
      const response = await fetch('https://api.avalai.ir/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gemini-3.1-flash-image-preview',
          messages: [{ role: 'user', content: prompt }],
          max_tokens: 2000,
          temperature: 0.7
        })
      });

      if (!response.ok) return null;

      const data = await response.json();
      const content = data.choices[0].message.content;
      const imageUrl = content.match(/https?:\/\/[^\s]+\.(?:png|jpg|jpeg|gif|webp)/i)?.[0];
      
      if (!imageUrl) return null;
      
      const imageResponse = await fetch(imageUrl);
      const blob = await imageResponse.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      return null;
    }
  };

  // ===== ۵. تولید صوت =====
  const generateAudio = async (text, apiKey) => {
    try {
      const response = await fetch('https://api.avalai.ir/v1/audio/speech', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gemini-2.5-flash-preview-tts',
          input: text.slice(0, 4000),
          voice: 'en-US-Standard-A',
          response_format: 'mp3',
          speed: 0.85
        })
      });

      if (!response.ok) return null;

      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      return null;
    }
  };

  // ===== ۶. ساخت HTML تدریس =====
  const buildLessonHTML = (lessonData, images, audioBase64) => {
    const stepsHTML = lessonData.steps?.map((step, index) => {
      const stepIcons = {
        start: '🌟', introduce: '🔍', practice: '✏️',
        game: '🎮', write: '🖌️', quiz: '📝', summary: '🎯'
      };
      const stepIcon = stepIcons[step.type] || '📚';
      
      let activityHTML = '';
      if (step.activity) {
        if (step.activity.type === 'question') {
          const optionsHTML = step.activity.options?.map((opt, i) => `
            <div class="ai-activity-option" data-correct="${opt === step.activity.correctAnswer ? 'true' : 'false'}" onclick="handleActivityClick(this)">
              <span>${String.fromCharCode(65 + i)}. ${opt}</span>
            </div>
          `).join('');
          activityHTML = `
            <div class="ai-activity-box">
              <p class="ai-activity-question">❓ ${step.activity.description}</p>
              <div class="ai-activity-options">${optionsHTML}</div>
              <div class="ai-activity-feedback"></div>
            </div>
          `;
        } else if (step.activity.type === 'game') {
          const gameItems = step.activity.options?.map((item, i) => `
            <div class="ai-game-item" data-id="${i}" onclick="handleGameClick(this, '${step.activity.correctAnswer}')">${item}</div>
          `).join('');
          activityHTML = `
            <div class="ai-activity-box ai-game-box">
              <p class="ai-activity-question">🎮 ${step.activity.description}</p>
              <div class="ai-game-content">${gameItems}</div>
              <div class="ai-game-feedback"></div>
            </div>
          `;
        } else {
          activityHTML = `
            <div class="ai-activity-box">
              <p class="ai-activity-question">✨ ${step.activity.description}</p>
            </div>
          `;
        }
      }

      const imageHTML = images[index] ? `
        <div class="ai-visual-box">
          <img src="${images[index]}" alt="تصویر ${step.title}" class="ai-step-image">
          <p class="ai-visual-caption">${step.visualDescription || ''}</p>
        </div>
      ` : '';

      return `
        <div class="ai-lesson-step" data-step="${index}">
          <div class="ai-step-header">
            <span class="ai-step-icon">${stepIcon}</span>
            <span class="ai-step-title">مرحله ${step.stepNumber}: ${step.title}</span>
          </div>
          <div class="ai-step-content">
            <div class="ai-teacher-talk">
              <div class="ai-teacher-avatar">👩‍🏫</div>
              <div class="ai-teacher-bubble">
                ${step.teacherTalk}
                ${step.encouragement ? `<div class="ai-encouragement">✨ ${step.encouragement} ✨</div>` : ''}
              </div>
            </div>
            ${imageHTML}
            ${activityHTML}
          </div>
        </div>
      `;
    }).join('') || '';

    const keywordsHTML = lessonData.keywords?.map(kw => `<span class="ai-keyword-tag">🔹 ${kw}</span>`).join('') || '';

    const lessonTypeLabels = {
      letters: 'حروف الفبا', numbers: 'اعداد', science: 'علوم',
      story: 'داستان', religious: 'دینی', social: 'اجتماعی', other: 'متفرقه'
    };
    const typeLabel = lessonTypeLabels[lessonData.lessonType] || 'آموزشی';

    const poemHTML = lessonData.poem ? `
      <div class="ai-poem-box">
        <div class="ai-poem-icon">📝</div>
        <div class="ai-poem-content">${lessonData.poem}</div>
      </div>
    ` : '';

    const homeworkHTML = lessonData.homework ? `
      <div class="ai-homework-box">
        <h3>🏠 تکلیف منزل</h3>
        <p>${lessonData.homework}</p>
      </div>
    ` : '';

    const audioHTML = audioBase64 ? `
      <div class="ai-audio-container">
        <p>🔊 گوش دادن به تدریس:</p>
        <audio controls>
          <source src="${audioBase64}" type="audio/mpeg">
        </audio>
      </div>
    ` : '';

    return `
<!DOCTYPE html>
<html lang="fa" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${lessonData.title || 'تدریس'}</title>
  <style>
    /* ===== تمام کلاس‌ها با پیشوند ai- ===== */
    .ai-lesson-container * { margin: 0; padding: 0; box-sizing: border-box; }
    
    @keyframes ai-gradientBG {
      0%, 100% { background-position: 0% 50%; }
      25% { background-position: 100% 50%; }
      50% { background-position: 100% 0%; }
      75% { background-position: 0% 100%; }
    }
    
    @keyframes ai-float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-12px); }
    }
    
    @keyframes ai-pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    
    @keyframes ai-slideUp {
      from { opacity: 0; transform: translateY(40px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes ai-bounceIn {
      0% { transform: scale(0); opacity: 0; }
      60% { transform: scale(1.15); opacity: 1; }
      100% { transform: scale(1); }
    }
    
    @keyframes ai-rainbowBorder {
      0% { border-color: #ff6b6b; }
      33% { border-color: #feca57; }
      66% { border-color: #48dbfb; }
      100% { border-color: #ff6b6b; }
    }
    
    @keyframes ai-confettiRain {
      0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
      100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
    }
    
    .ai-lesson-container body {
      font-family: 'Vazir', 'Segoe UI', Tahoma, sans-serif;
      background: linear-gradient(135deg, #fdfcfb 0%, #e2d1c3 50%, #f0e6d3 100%);
      background-size: 400% 400%;
      animation: ai-gradientBG 20s ease infinite;
      padding: 20px;
      min-height: 100vh;
    }
    
    .ai-lesson-container {
      max-width: 950px;
      margin: 0 auto;
      background: rgba(255, 255, 255, 0.92);
      backdrop-filter: blur(10px);
      border-radius: 50px 50px 30px 30px;
      padding: 40px 35px 50px;
      box-shadow: 0 30px 80px rgba(0,0,0,0.12);
      border: 3px solid transparent;
      animation: ai-rainbowBorder 8s ease infinite;
    }
    
    .ai-lesson-title {
      text-align: center;
      font-size: 2.8rem;
      font-weight: 900;
      background: linear-gradient(135deg, #f093fb, #f5576c, #4facfe);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-bottom: 8px;
      animation: ai-float 4s ease-in-out infinite;
    }
    
    .ai-lesson-subtitle {
      text-align: center;
      font-size: 1.4rem;
      color: #555;
      margin-bottom: 10px;
      font-weight: 600;
    }
    
    .ai-lesson-type-badge {
      text-align: center;
      background: linear-gradient(135deg, #ffecd2, #fcb69f);
      padding: 8px 24px;
      border-radius: 60px;
      display: inline-block;
      margin: 0 auto 20px;
      font-size: 1.1rem;
      font-weight: 700;
      color: #5a2d0c;
      box-shadow: 0 6px 20px rgba(252, 182, 159, 0.4);
    }
    
    .ai-poem-box {
      background: linear-gradient(135deg, #ffeaa7, #fdcb6e);
      border-radius: 30px;
      padding: 25px 30px;
      margin-bottom: 30px;
      border-right: 8px solid #e17055;
      display: flex;
      align-items: flex-start;
      gap: 18px;
    }
    
    .ai-poem-icon { font-size: 2.5rem; animation: ai-float 3s ease-in-out infinite; }
    .ai-poem-content { font-size: 1.2rem; line-height: 2.2; color: #2d3436; font-weight: 500; }
    
    .ai-lesson-step {
      background: white;
      border-radius: 35px 35px 25px 25px;
      padding: 28px 30px;
      margin-bottom: 30px;
      box-shadow: 0 8px 25px rgba(0,0,0,0.06);
      border: 3px solid #f0f0f0;
      transition: all 0.3s ease;
      animation: ai-slideUp 0.6s ease forwards;
      animation-delay: ${(index) => index * 0.1}s;
      opacity: 0;
    }
    
    .ai-lesson-step:hover {
      transform: translateY(-4px);
      box-shadow: 0 15px 40px rgba(0,0,0,0.1);
      border-color: #f093fb;
    }
    
    .ai-step-header {
      display: flex;
      align-items: center;
      gap: 14px;
      margin-bottom: 18px;
      background: linear-gradient(135deg, #f8f9ff, #f0f4ff);
      padding: 12px 20px;
      border-radius: 60px;
    }
    
    .ai-step-icon { font-size: 2rem; animation: ai-float 3s ease-in-out infinite; }
    .ai-step-title { font-size: 1.4rem; font-weight: 800; color: #2d3436; }
    
    .ai-step-content { padding: 10px 5px; }
    
    .ai-teacher-talk {
      display: flex;
      align-items: flex-start;
      gap: 18px;
      margin: 15px 0 20px;
      background: #f8f9fa;
      padding: 20px 25px;
      border-radius: 30px 30px 30px 10px;
      border-right: 6px solid #6c5ce7;
    }
    
    .ai-teacher-avatar { font-size: 3rem; flex-shrink: 0; animation: ai-float 3s ease-in-out infinite; }
    
    .ai-teacher-bubble {
      font-size: 1.15rem;
      line-height: 2.2;
      color: #2d3436;
      flex: 1;
    }
    
    .ai-encouragement {
      margin-top: 12px;
      font-size: 1.3rem;
      color: #00b894;
      font-weight: 700;
      background: #e0f7f1;
      padding: 8px 18px;
      border-radius: 40px;
      display: inline-block;
      animation: ai-pulse 1.5s ease-in-out infinite;
    }
    
    .ai-visual-box {
      margin: 20px 0;
      text-align: center;
      background: #fafafa;
      padding: 20px;
      border-radius: 30px;
      border: 2px dashed #ddd;
    }
    
    .ai-step-image {
      max-width: 100%;
      max-height: 350px;
      border-radius: 25px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.1);
      transition: transform 0.5s ease;
    }
    
    .ai-step-image:hover { transform: scale(1.03) rotate(-1deg); }
    .ai-visual-caption { margin-top: 12px; color: #555; font-size: 1.05rem; font-weight: 500; }
    
    .ai-activity-box {
      background: linear-gradient(135deg, #f0f8ff, #e8f0fe);
      border-radius: 25px;
      padding: 22px 28px;
      margin: 18px 0;
      border: 3px solid #a8d8ea;
    }
    
    .ai-activity-question { font-size: 1.2rem; font-weight: 700; color: #2d3436; margin-bottom: 15px; }
    
    .ai-activity-options {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      justify-content: center;
    }
    
    .ai-activity-option {
      background: white;
      padding: 12px 25px;
      border-radius: 50px;
      font-size: 1.1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      border: 3px solid #dfe6e9;
      box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    }
    
    .ai-activity-option:hover {
      transform: scale(1.05) translateY(-3px);
      border-color: #6c5ce7;
    }
    
    .ai-activity-option.correct { background: #00b894; color: white; border-color: #00b894; animation: ai-bounceIn 0.5s ease; }
    .ai-activity-option.wrong { background: #ff6b6b; color: white; border-color: #ff6b6b; animation: ai-pulse 0.5s ease; }
    
    .ai-game-content {
      display: flex;
      flex-wrap: wrap;
      gap: 15px;
      justify-content: center;
    }
    
    .ai-game-item {
      background: white;
      padding: 18px 28px;
      border-radius: 25px;
      font-size: 1.5rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.3s ease;
      border: 4px solid #dfe6e9;
      box-shadow: 0 6px 20px rgba(0,0,0,0.05);
      min-width: 80px;
      text-align: center;
    }
    
    .ai-game-item:hover {
      transform: scale(1.1) rotate(-2deg);
      border-color: #fdcb6e;
    }
    
    .ai-game-item.correct { border-color: #00b894; background: #00b894; color: white; animation: ai-bounceIn 0.5s ease; }
    .ai-game-item.wrong { border-color: #ff6b6b; background: #ff6b6b; color: white; animation: ai-pulse 0.5s ease; }
    
    .ai-activity-feedback, .ai-game-feedback { margin-top: 15px; font-size: 1.2rem; font-weight: 700; text-align: center; }
    .ai-feedback-correct { color: #00b894; animation: ai-bounceIn 0.5s ease; }
    .ai-feedback-wrong { color: #ff6b6b; animation: ai-pulse 0.5s ease; }
    
    .ai-keywords-section {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      justify-content: center;
      margin: 25px 0 15px;
      padding: 20px;
      background: #f8f9fa;
      border-radius: 30px;
    }
    
    .ai-keyword-tag {
      background: linear-gradient(135deg, #f093fb, #f5576c);
      color: white;
      padding: 8px 20px;
      border-radius: 40px;
      font-weight: 700;
      font-size: 1.05rem;
      box-shadow: 0 4px 15px rgba(245, 87, 108, 0.3);
      animation: ai-float 4s ease-in-out infinite;
    }
    
    .ai-audio-container {
      text-align: center;
      margin: 20px 0;
      padding: 20px;
      background: #f0f4ff;
      border-radius: 30px;
    }
    
    .ai-audio-container audio {
      width: 100%;
      max-width: 400px;
      border-radius: 30px;
    }
    
    .ai-homework-box {
      background: linear-gradient(135deg, #fd79a8, #e84393);
      border-radius: 30px;
      padding: 25px 30px;
      margin: 25px 0;
      color: white;
      text-align: center;
    }
    
    .ai-homework-box h3 { color: white; margin-bottom: 10px; }
    .ai-homework-box p { color: white; font-size: 1.1rem; line-height: 2; }
    
    .ai-btn-group {
      display: flex;
      gap: 18px;
      justify-content: center;
      margin-top: 35px;
      flex-wrap: wrap;
    }
    
    .ai-btn {
      padding: 16px 40px;
      border: none;
      border-radius: 60px;
      font-size: 1.2rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.3s ease;
      color: white;
      box-shadow: 0 8px 25px rgba(0,0,0,0.1);
    }
    
    .ai-btn-print { background: linear-gradient(135deg, #6c5ce7, #a29bfe); }
    .ai-btn-download { background: linear-gradient(135deg, #00b894, #55efc4); }
    
    .ai-btn:hover { transform: translateY(-5px) scale(1.03); box-shadow: 0 15px 40px rgba(0,0,0,0.15); }
    
    .ai-confetti-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      overflow: hidden;
    }
    
    .ai-confetti-piece {
      position: absolute;
      top: -20px;
      font-size: 2rem;
      animation: ai-confettiRain 4s linear forwards;
    }
    
    @media (max-width: 768px) {
      .ai-lesson-container { padding: 25px 18px; }
      .ai-lesson-title { font-size: 2rem; }
      .ai-step-title { font-size: 1.1rem; }
      .ai-teacher-talk { flex-direction: column; align-items: center; text-align: center; }
      .ai-teacher-bubble { font-size: 1rem; }
      .ai-activity-option { padding: 10px 18px; font-size: 0.95rem; }
      .ai-game-item { padding: 14px 20px; font-size: 1.2rem; min-width: 60px; }
      .ai-btn { padding: 12px 28px; font-size: 1rem; }
      .ai-poem-box { flex-direction: column; align-items: center; text-align: center; }
    }
  </style>
</head>
<body>
  <div class="ai-lesson-container" id="lesson-container">
    <h1 class="ai-lesson-title">${lessonData.title || '📚 تدریس'}</h1>
    <p class="ai-lesson-subtitle">👶 مناسب برای کودکان ${childAge} ساله</p>
    <div style="text-align:center;">
      <span class="ai-lesson-type-badge">📌 نوع درس: ${typeLabel}</span>
    </div>
    
    ${poemHTML}
    
    ${stepsHTML}
    
    <div class="ai-keywords-section">
      <span style="font-weight:700;color:#2d3436;">📝 کلمات کلیدی:</span>
      ${keywordsHTML}
    </div>
    
    ${audioHTML}
    ${homeworkHTML}
    
    <div class="ai-btn-group">
      <button class="ai-btn ai-btn-print" onclick="window.print()">🖨️ چاپ</button>
      <button class="ai-btn ai-btn-download" onclick="downloadLesson()">📥 دانلود HTML</button>
    </div>
  </div>

  <script>
    function handleActivityClick(element) {
      if (element.classList.contains('correct') || element.classList.contains('wrong')) return;
      
      const isCorrect = element.dataset.correct === 'true';
      const feedback = element.closest('.ai-activity-box').querySelector('.ai-activity-feedback');
      
      if (isCorrect) {
        element.classList.add('correct');
        feedback.innerHTML = '<span class="ai-feedback-correct">🎉 آفرین! پاسخ درست است!</span>';
        launchConfetti();
      } else {
        element.classList.add('wrong');
        feedback.innerHTML = '<span class="ai-feedback-wrong">❌ نه، دوباره تلاش کن!</span>';
        setTimeout(() => { element.classList.remove('wrong'); feedback.innerHTML = ''; }, 1500);
      }
    }
    
    function handleGameClick(element, correctAnswer) {
      if (element.classList.contains('correct') || element.classList.contains('wrong')) return;
      
      const isCorrect = element.textContent.trim() === correctAnswer;
      const feedback = element.closest('.ai-game-box').querySelector('.ai-game-feedback');
      
      if (isCorrect) {
        element.classList.add('correct');
        feedback.innerHTML = '<span class="ai-feedback-correct">🎉 آفرین! درست انتخاب کردی!</span>';
        launchConfetti();
      } else {
        element.classList.add('wrong');
        feedback.innerHTML = '<span class="ai-feedback-wrong">❌ نه! دوباره امتحان کن!</span>';
        setTimeout(() => { element.classList.remove('wrong'); feedback.innerHTML = ''; }, 1500);
      }
    }
    
    function launchConfetti() {
      const container = document.createElement('div');
      container.className = 'ai-confetti-container';
      const emojis = ['🎉', '⭐', '🌟', '🎊', '💫', '✨', '🎈', '🏆'];
      for (let i = 0; i < 40; i++) {
        const piece = document.createElement('div');
        piece.className = 'ai-confetti-piece';
        piece.textContent = emojis[Math.floor(Math.random() * emojis.length)];
        piece.style.left = Math.random() * 100 + '%';
        piece.style.animationDuration = (3 + Math.random() * 3) + 's';
        piece.style.animationDelay = (Math.random() * 0.5) + 's';
        piece.style.fontSize = (1.5 + Math.random() * 2.5) + 'rem';
        container.appendChild(piece);
      }
      document.body.appendChild(container);
      setTimeout(() => container.remove(), 5000);
    }
    
    function downloadLesson() {
      const container = document.getElementById('lesson-container');
      const styles = document.querySelector('style').innerHTML;
      const fullHTML = \`<!DOCTYPE html>
<html lang="fa" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${lessonData.title || 'تدریس'}</title>
  <style>\${styles}</style>
</head>
<body>\${container.outerHTML}</body>
</html>\`;
      const blob = new Blob([fullHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '${lessonData.title || 'تدریس'}.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  </script>
</body>
</html>
    `;
  };

  // ===== ۷. تابع اصلی تولید تدریس =====
  const handleGenerate = async () => {
    if (!pdfFile || !chapter || !apiKey) {
      setError('لطفاً همهٔ فیلدها را پر کنید!');
      return;
    }

    setLoading(true);
    setError('');
    setProgress('📖 مرحله ۱: استخراج متن از PDF...');

    try {
      const text = await extractTextFromPDF(pdfFile);
      setProgress('🧠 مرحله ۲: تحلیل محتوا و طراحی تدریس بر اساس PDF...');

      const prompt = generateSmartPrompt(text, grade, book, chapter, childAge);
      const lessonData = await generateLesson(prompt, apiKey);
      
      setProgress('🎨 مرحله ۳: تولید تصاویر...');
      const images = [];
      for (let i = 0; i < (lessonData.steps?.length || 4); i++) {
        const step = lessonData.steps?.[i];
        const imgPrompt = step?.visualDescription || `تصویر کودکانه برای مرحله ${i+1} درس ${lessonData.title || 'تدریس'}`;
        const img = await generateImage(imgPrompt, apiKey);
        images.push(img || '');
      }

      setProgress('🎵 مرحله ۴: تولید صوت...');
      let audioBase64 = null;
      try {
        const allText = lessonData.steps?.map(s => s.teacherTalk).join('\n') || '';
        if (allText) {
          audioBase64 = await generateAudio(allText, apiKey);
        }
      } catch (e) { console.warn('تولید صوت با خطا مواجه شد'); }

      setProgress('📦 مرحله ۵: ساخت فایل HTML نهایی...');
      const finalHTML = buildLessonHTML(lessonData, images, audioBase64);
      
      const blob = new Blob([finalHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      setDownloadLink(url);

      setProgress('✅ تدریس کامل بر اساس PDF ساخته شد!');
      setError('');

    } catch (err) {
      console.error('❌ خطا:', err);
      setError(`متأسفانه خطایی رخ داد: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ===== رندر با کلاس‌های ایزوله =====
  return (
    <div className="ai-lesson-generator-container">
      <h1>🎓 کارخانهٔ تولید تدریس هوشمند</h1>
      <p className="ai-subtitle">تدریس گام‌به‌گام بر اساس محتوای PDF، مانند یک معلم با ۳۰ سال تجربه</p>

      <div className="ai-form-section">
        <div className="ai-form-group">
          <label>🔑 کلید API AvalAI</label>
          <input
            type="password"
            placeholder="کلید API خود را وارد کنید"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </div>

        <div className="ai-form-row">
          <div className="ai-form-group ai-half">
            <label>📚 پایه تحصیلی</label>
            <select value={grade} onChange={(e) => setGrade(e.target.value)}>
              <option value="">انتخاب کنید</option>
              <option value="اول">اول دبستان</option>
              <option value="دوم">دوم دبستان</option>
              <option value="سوم">سوم دبستان</option>
              <option value="چهارم">چهارم دبستان</option>
              <option value="پنجم">پنجم دبستان</option>
              <option value="ششم">ششم دبستان</option>
            </select>
          </div>

          <div className="ai-form-group ai-half">
            <label>📖 نام کتاب درسی</label>
            <input type="text" placeholder="مثلاً: فارسی، ریاضی، علوم، ..." value={book} onChange={(e) => setBook(e.target.value)} />
          </div>
        </div>

        <div className="ai-form-row">
          <div className="ai-form-group ai-half">
            <label>📑 عنوان یا شماره فصل</label>
            <input type="text" placeholder="مثلاً: درس اول، فصل ۳، ..." value={chapter} onChange={(e) => setChapter(e.target.value)} />
          </div>

          <div className="ai-form-group ai-half">
            <label>👶 سن کودک</label>
            <input type="number" min="6" max="12" value={childAge} onChange={(e) => setChildAge(e.target.value)} />
          </div>
        </div>

        <div className="ai-form-group">
          <label>🧒 نام کودک (اختیاری، برای شخصی‌سازی)</label>
          <input type="text" placeholder="مثلاً: علی، سارا، ..." value={childName} onChange={(e) => setChildName(e.target.value)} />
        </div>

        <div className="ai-form-group">
          <label>📎 آپلود فایل PDF درس</label>
          <div className="ai-file-upload">
            <input type="file" accept=".pdf" onChange={(e) => setPdfFile(e.target.files[0])} id="ai-pdf-upload" />
            <label htmlFor="ai-pdf-upload" className="ai-file-label">
              {pdfFile ? pdfFile.name : '📤 انتخاب فایل PDF'}
            </label>
          </div>
        </div>

        <button onClick={handleGenerate} disabled={loading} className="ai-generate-btn">
          {loading ? '⏳ در حال تولید...' : '🎯 شروع تولید تدریس هوشمند'}
        </button>

        {progress && (
          <div className="ai-progress-box">
            <p>{progress}</p>
            {loading && <div className="ai-loading-bar"><div className="ai-loading-fill"></div></div>}
          </div>
        )}

        {error && (
          <div className="ai-error-box">
            <p>❌ {error}</p>
          </div>
        )}

        {downloadLink && (
          <div className="ai-success-box">
            <p>✅ تدریس کامل بر اساس PDF ساخته شد!</p>
            <a href={downloadLink} download={`${chapter}_${book}_${grade}.html`} className="ai-lesson-link">
              📥 دانلود فایل HTML تدریس
            </a>
          </div>
        )}
      </div>

      <div className="ai-features-section">
        <h3>✨ ویژگی‌های تدریس هوشمند</h3>
        <div className="ai-features-grid">
          <div className="ai-feature-item">📄 تحلیل واقعی PDF</div>
          <div className="ai-feature-item">🧠 تدریس بر اساس محتوا</div>
          <div className="ai-feature-item">📚适用 برای همهٔ کتاب‌ها</div>
          <div className="ai-feature-item">👩‍🏫 تدریس گام‌به‌گام</div>
          <div className="ai-feature-item">🎨 انیمیشن‌های فراوان</div>
          <div className="ai-feature-item">🔊 صوت با گوینده</div>
          <div className="ai-feature-item">🖼️ تصاویر اختصاصی</div>
          <div className="ai-feature-item">🎮 بازی‌های تعاملی</div>
          <div className="ai-feature-item">📝 آزمون هوشمند</div>
          <div className="ai-feature-item">💬 گفتار کودکانه</div>
          <div className="ai-feature-item">⭐ تشویق‌های لحظه‌ای</div>
          <div className="ai-feature-item">🌈 طراحی شاد و جذاب</div>
        </div>
      </div>
    </div>
  );
};

export default AILessonGenerator;